import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import IdBadge from "components/IdBadge";
import { useMemo } from "react";
import { useLidarClearance } from "./LidarClearanceContext";
import type { LidarClearanceAxis } from "./schemas";
import t from "./translations";

type Props = {
  region: string;
  excludeAxisIds: Set<number>;
  value: number | null;
  onChange: (axisId: number | null) => void;
};

export default function AxisPicker({
  region,
  excludeAxisIds,
  value,
  onChange,
}: Props) {
  const { axes } = useLidarClearance();

  const options = useMemo(
    () =>
      axes
        .filter(
          (axis) => axis.region === region && !excludeAxisIds.has(axis.id)
        )
        .sort((a, b) => a.name_en.localeCompare(b.name_en)),
    [axes, region, excludeAxisIds]
  );

  const selected = options.find((axis) => axis.id === value) ?? null;

  return (
    <Autocomplete<LidarClearanceAxis>
      options={options}
      value={selected}
      onChange={(_event, next) => onChange(next?.id ?? null)}
      getOptionLabel={(option) => t.AxisOptionLabel(option.name_en, option.id)}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.id}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {option.name_en}
          </Typography>
          <IdBadge prefix={option.region.toUpperCase()} id={option.id} />
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={t.AxisLabel} />}
    />
  );
}
